// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const MONTH_NAMES = MONTHS;

export function number(value: number | null, digits = 0): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return value.toLocaleString('en-AU', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/**
 * Concentrations span four orders of magnitude — a clean hour is 3 µg/m³ and the record is
 * 13,516. A fixed decimal count renders the clean hour as "3.0" and the record as
 * "13516.2"; both are wrong for their own magnitude.
 */
export function concentration(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  if (Math.abs(value) >= 1000) return number(value, 0);
  if (Math.abs(value) >= 100) return number(value, 0);
  if (Math.abs(value) >= 10) return number(value, 1);
  return number(value, 1);
}

export function percent(value: number | null, digits = 1): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

export function pollutantLabel(pollutant: string): string {
  return pollutant === 'pm2.5' ? 'PM2.5' : pollutant === 'pm10' ? 'PM10' : pollutant.toUpperCase();
}

/** Every concentration on this site is µg/m³ — gases are never compared across borders. */
export const UNIT = 'µg/m³';

const DAY_MS = 86_400_000;

export function dayNumberToYmd(day: number): string {
  const date = new Date(day * DAY_MS);
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
    date.getUTCDate()
  ).padStart(2, '0')}`;
}

export function prettyDate(ymd: string): string {
  const [year, month, day] = ymd.slice(0, 10).split('-');
  return `${Number(day)} ${MONTHS[Number(month) - 1]} ${year}`;
}

/** '2019-12-21T14:00' → '21 Dec 2019, 2pm'. Local time in the station's own jurisdiction. */
export function prettyHour(stamp: string): string {
  const date = prettyDate(stamp);
  const hour = Number(stamp.slice(11, 13));
  return `${date}, ${hourLabel(hour)}`;
}

export function hourLabel(hour: number): string {
  if (hour === 0) return '12am';
  if (hour === 12) return '12pm';
  return hour < 12 ? `${hour}am` : `${hour - 12}pm`;
}

/** Hour-of-day as the interval it covers, which is what the sources actually measure. */
export function hourRange(hour: number): string {
  return `${hourLabel(hour)}–${hourLabel((hour + 1) % 24)}`;
}

export function duration(hours: number): string {
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return rest ? `${days} d ${rest} h` : `${days} d`;
}

export function ordinal(n: number): string {
  const suffix = n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th';
  return `${n}${suffix}`;
}

/**
 * Tooltip text for the [data-tip] utility. setAttribute takes RAW newlines — encoding them as
 * `&#10;` here puts the literal characters on screen, and the reverse mistake (raw newlines in
 * an innerHTML string) collapses the tooltip to one line. Two different escapes, two different
 * places; this is the setAttribute one.
 */
export function tip(...lines: Array<string | null | undefined>): string {
  return lines.filter(Boolean).join('\n');
}

/** For an attribute written into an innerHTML string, where a raw newline would collapse. */
export function tipAttr(...lines: Array<string | null | undefined>): string {
  return lines
    .filter(Boolean)
    .join('&#10;')
    .replace(/"/g, '&quot;');
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
